/**
 * 动态 PWA 信息管理
 * -----------------
 * 默认（主页）：manifest 由 public/manifest.json 静态提供，name=ZHSOS、icon=logo.svg。
 *
 * 话题页：用「主话题」的 title + icon 生成 manifest，经 Service Worker 在
 *   /pwa/manifest.json?key=topic:{slug} 用【真实同源 URL】返回，并切换 <link rel=manifest> 到该 URL。
 *   原因：浏览器「安装到桌面」只认真实同源 URL 的 manifest；blob:/data: 的 manifest
 *   不被安装识别，会导致安装时仍显示主页信息。所以必须 SW 代发 + 同源 URL。
 *   跨域图床的话题图标也经 /pwa/icon 代理成同源，确保安装图标可用。
 *
 * 离开话题页：恢复主页 manifest（/manifest.json）。
 * 不动 document.title（由各页面自行管理）。
 */

const DEFAULT_THEME_COLOR = '#4f46e5'
const HOME_MANIFEST_HREF = '/manifest.json' // 静态主页 manifest
const TOPIC_MANIFEST_PATH = '/pwa/manifest.json' // SW 代发的同源话题 manifest
const ICON_PROXY = '/pwa/icon'

let _homeSnapshot = null // 首次切话题前抓取的主页 PWA 配置快照，供恢复用
let _lastTopic = null // 最近一次设置的话题 manifest（{manifest, key}），供 SW 接管/换新时重发
let _controllerBound = false // 是否已绑定 controllerchange 监听（全局只绑一次）

function queryLink(rel) {
  return document.querySelector(`link[rel~="${rel}"]`)
}

function guessImageType(url) {
  const u = String(url || '').toLowerCase().split('?')[0]
  if (u.endsWith('.svg')) return 'image/svg+xml'
  if (u.endsWith('.png')) return 'image/png'
  if (u.endsWith('.jpg') || u.endsWith('.jpeg')) return 'image/jpeg'
  if (u.endsWith('.webp')) return 'image/webp'
  if (u.endsWith('.gif')) return 'image/gif'
  return 'image/png'
}

// 统一设置一个 <link>：缺失则创建。type/sizes 为空则移除该属性，避免残留误导浏览器
function setLink(rel, href, type, sizes) {
  let el = queryLink(rel)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
  if (type) el.setAttribute('type', type)
  else el.removeAttribute('type')
  if (sizes) el.setAttribute('sizes', sizes)
  else el.removeAttribute('sizes')
  return el
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

// 抓取主页 PWA 配置快照（manifest / icon / apple-touch-icon / theme-color 的原始值）
function snapshotHome() {
  if (_homeSnapshot) return
  const manifest = queryLink('manifest')
  const icon = queryLink('icon')
  const apple = queryLink('apple-touch-icon')
  const theme = document.querySelector('meta[name="theme-color"]')
  _homeSnapshot = {
    manifestHref: manifest?.getAttribute('href') || HOME_MANIFEST_HREF,
    iconHref: icon?.getAttribute('href') || '/favicon.svg',
    iconType: icon?.getAttribute('type') || 'image/svg+xml',
    iconSizes: icon?.getAttribute('sizes') || '',
    appleHref: apple?.getAttribute('href') || '/logo.png',
    themeColor: theme?.getAttribute('content') || DEFAULT_THEME_COLOR,
  }
}

// 跨域图床图标 → 同源代理 URL；本地图标原样返回
function proxyIconIfRemote(raw) {
  if (!raw) return '/logo.svg'
  if (/^https?:/i.test(raw)) return `${ICON_PROXY}?src=${encodeURIComponent(raw)}`
  return raw
}

function buildManifest({ name, shortName, icon, startUrl }) {
  const proxied = proxyIconIfRemote(icon)
  const type = guessImageType(icon)
  return {
    name,
    short_name: shortName,
    description: name,
    start_url: startUrl || '/',
    scope: '/',
    display: 'standalone',
    orientation: 'portrait',
    background_color: '#ffffff',
    theme_color: DEFAULT_THEME_COLOR,
    lang: 'zh-CN',
    dir: 'ltr',
    icons: [
      { src: proxied, sizes: 'any', type, purpose: 'any' },
      { src: proxied, sizes: 'any', type, purpose: 'maskable' },
      { src: '/logo.svg', sizes: 'any', type: 'image/svg+xml', purpose: 'any' },
    ],
  }
}

// 同步切换可见的图标与颜色：favicon、iOS 主屏图标、地址栏主题色
function applyVisibleIcons(icon) {
  setLink('icon', icon, guessImageType(icon))
  setLink('apple-touch-icon', icon)
  setMeta('theme-color', DEFAULT_THEME_COLOR)
}

// 把 manifest 推给 SW 缓存，并切换 <link rel=manifest> 到同源 URL
function publishTopicManifest(manifest, key) {
  const controller = navigator.serviceWorker.controller
  if (controller) controller.postMessage({ type: 'set-pwa-manifest', key, manifest })
  // 切到同源 manifest URL：key 让 SW 取对应 manifest；v 强制浏览器重取并重评估安装性
  const nonce = Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
  setLink('manifest', `${TOPIC_MANIFEST_PATH}?key=${encodeURIComponent(key)}&v=${nonce}`, 'application/manifest+json')
}

/**
 * 话题页：设置主话题的 PWA 信息。
 * @param {{title?:string, icon?:string, slug?:string|number, id?:number}} topic
 */
export function setTopicPwa(topic) {
  if (typeof document === 'undefined' || !topic) return
  snapshotHome()

  const title = topic.title || 'ZHSOS'
  const shortName = title.length > 12 ? Array.from(title).slice(0, 12).join('') : title
  const icon = topic.icon || '/logo.svg'
  const startUrl = '/t/' + (topic.slug || topic.id || '')
  const key = 'topic:' + (topic.slug || topic.id || '')
  const manifest = buildManifest({ name: title, shortName, icon, startUrl })
  _lastTopic = { manifest, key }

  // 可见图标立即切换（favicon / apple-touch-icon 走 <img> 式获取，不需要 CORS）
  applyVisibleIcons(icon)

  // 无 SW（非安全上下文）时无法做话题级 manifest，到此为止
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !window.isSecureContext) return

  // SW 已接管：立即 publish（postMessage 把 manifest 推给 SW，并切到同源 manifest URL）
  if (navigator.serviceWorker.controller) publishTopicManifest(manifest, key)

  // 兜底：SW 尚未接管（首次加载）、或旧→新 SW 切换（本页加载期间新 SW 才 claim）时，
  // controllerchange 触发后用新 SW 重发一次，避免安装仍显示主页信息
  if (!_controllerBound) {
    _controllerBound = true
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (_lastTopic) publishTopicManifest(_lastTopic.manifest, _lastTopic.key)
    })
  }
}

/** 恢复主页 PWA 信息（离开话题页时调用） */
export function setHomePwa() {
  if (typeof document === 'undefined') return
  _lastTopic = null // 离开话题页：controllerchange 不再重发话题 manifest
  snapshotHome()
  // 恢复主页 manifest：静态同源文件，浏览器安装识别正常
  setLink('manifest', _homeSnapshot.manifestHref, 'application/manifest+json')
  setLink('icon', _homeSnapshot.iconHref, _homeSnapshot.iconType, _homeSnapshot.iconSizes)
  setLink('apple-touch-icon', _homeSnapshot.appleHref)
  setMeta('theme-color', _homeSnapshot.themeColor)
}

/**
 * 注册 service worker（仅安全上下文）。失败静默，不影响主功能。
 */
export function registerServiceWorker() {
  if (typeof window === 'undefined') return
  if (!('serviceWorker' in navigator)) return
  if (!window.isSecureContext) return // file://、非 HTTPS 不支持 SW
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {})
  })
}
